import type {
  MarkNotificationsReadInput,
  MarkNotificationsReadResult,
  NotificationItem,
  NotificationPreferences,
  Paginated,
  UnreadNotificationCount,
  UpdateNotificationPreferencesInput,
} from '@hemmafixa/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// The signed-in user's notifications (P4-08, API from P4-01). The same for every area.

const notificationsKey = ['notifications'] as const;
/** How often the bell asks for new notifications while the tab is open */
const UNREAD_POLL_MS = 60_000;

export function useUnreadCount() {
  return useQuery({
    queryKey: [...notificationsKey, 'unread-count'],
    queryFn: () => api<UnreadNotificationCount>('/notifications/unread-count'),
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

export function useNotifications(
  params: { unread?: boolean; page?: number; pageSize?: number },
  enabled = true,
) {
  const search = new URLSearchParams({
    page: String(params.page ?? 1),
    pageSize: String(params.pageSize ?? 20),
    ...(params.unread ? { unread: 'true' } : {}),
  });
  return useQuery({
    queryKey: [...notificationsKey, 'list', params],
    queryFn: () => api<Paginated<NotificationItem>>(`/notifications?${search}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MarkNotificationsReadInput) =>
      api<MarkNotificationsReadResult>('/notifications/read', { method: 'POST', body: input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: notificationsKey }),
  });
}

const preferencesKey = [...notificationsKey, 'preferences'] as const;

export function useNotificationPreferences() {
  return useQuery({
    queryKey: preferencesKey,
    queryFn: () => api<NotificationPreferences>('/notifications/preferences'),
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateNotificationPreferencesInput) =>
      api<NotificationPreferences>('/notifications/preferences', { method: 'PATCH', body: input }),
    onSuccess: (saved) => queryClient.setQueryData(preferencesKey, saved),
  });
}
